/* Product-specific selection and keyboard support, including AJAX quick view. */
(() => {
  if (customElements.get('spike-size-guide')) return;

  class SpikeSizeGuide extends HTMLElement {
    connectedCallback() {
      this.popup = this.closest('[data-size-chart-popup]');
      if (!this.popup) return;
      this.scope = this.closest('[data-quick-view-popup]') || document.getElementById('MainContent') || document;
      this.onChange = event => {
        const picker = event.target.closest('.product-option');
        if (this.scope.contains(event.target) && picker?.dataset.product === this.dataset.productId) {
          this.picker = picker;
          this.syncSelection();
        }
      };
      this.onKey = event => this.handleKey(event);
      document.addEventListener('change', this.onChange);
      document.addEventListener('keydown', this.onKey, true);
      this.observer = new MutationObserver(() => this.syncOpenState());
      this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.observer.observe(this.popup, { attributes: true, attributeFilter: ['class'] });
      this.syncSelection();
      this.syncOpenState();
    }

    disconnectedCallback() {
      clearTimeout(this.focusTimer);
      document.removeEventListener('change', this.onChange);
      document.removeEventListener('keydown', this.onKey, true);
      this.observer?.disconnect();
      if (this.isOpen) document.body.classList.remove('size-chart-show');
    }

    syncSelection() {
      const picker = this.picker?.isConnected ? this.picker : this.scope.querySelector(`.product-option[data-product="${CSS.escape(this.dataset.productId)}"]`);
      const option = picker?.querySelector(`[data-option-index="${CSS.escape(this.dataset.optionIndex)}"]`);
      const selected = option?.querySelector('input:checked')?.value
        ?? (option?.matches('select') ? option.value : option?.querySelector('select')?.value)
        ?? this.dataset.selected;
      this.querySelectorAll('[data-size-value]').forEach(row => {
        const active = row.dataset.sizeValue === selected;
        row.classList.toggle('is-selected', active);
        if (active) row.setAttribute('aria-current', 'true');
        else row.removeAttribute('aria-current');
      });
      if (this.isOpen) requestAnimationFrame(() => {
        if (!this.isOpen) return;
        const wrap = this.querySelector('.spike-size-guide__table-wrap');
        const row = wrap?.querySelector('[aria-current="true"]');
        if (!row) return;
        const viewport = wrap.getBoundingClientRect();
        const selectedRow = row.getBoundingClientRect();
        const header = wrap.querySelector('thead')?.getBoundingClientRect().height || 0;
        if (selectedRow.bottom > viewport.bottom) wrap.scrollTop += selectedRow.bottom - viewport.bottom + 4;
        else if (selectedRow.top < viewport.top + header) wrap.scrollTop -= viewport.top + header - selectedRow.top + 4;
      });
    }

    open(trigger) {
      this.returnFocus = trigger;
      const picker = trigger.closest('.product-option');
      if (picker?.dataset.product === this.dataset.productId) this.picker = picker;
      document.querySelectorAll('[data-size-chart-popup].is-show').forEach(popup => {
        if (popup !== this.popup) popup.classList.remove('is-show');
      });
      document.body.classList.add('size-chart-show');
      this.popup.classList.add('is-show');
      this.syncOpenState();
    }

    close() {
      document.body.classList.remove('size-chart-show');
      this.popup.classList.remove('is-show');
      this.syncOpenState();
    }

    syncOpenState() {
      const open = document.body.classList.contains('size-chart-show') && this.popup.classList.contains('is-show');
      this.popup.setAttribute('aria-hidden', String(!open));
      if (open === this.isOpen) return;
      this.isOpen = open;
      if (open) {
        this.syncSelection();
        this.returnFocus ??= document.activeElement;
        this.focusHeading();
      } else if (this.returnFocus?.isConnected) {
        clearTimeout(this.focusTimer);
        this.returnFocus.focus({ preventScroll: true });
        this.returnFocus = null;
      }
    }

    focusHeading(attempt = 0) {
      if (!this.isOpen || !this.isConnected || this.popup.contains(document.activeElement)) return;
      this.querySelector('h2')?.focus({ preventScroll: true });
      // Quick view loads its styles asynchronously. A focus attempt while the
      // dialog still inherits visibility:hidden is ignored by the browser.
      if (!this.popup.contains(document.activeElement) && attempt < 20) {
        this.focusTimer = setTimeout(() => this.focusHeading(attempt + 1), 50);
      }
    }

    handleKey(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
      } else if (event.key === 'Tab') {
        const controls = [...this.popup.querySelectorAll('a[href], button, summary, [tabindex="0"]')]
          .filter(el => !el.disabled && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        if (!controls.length) return;
        const current = controls.indexOf(document.activeElement);
        if (current === -1 || (event.shiftKey && current === 0) || (!event.shiftKey && current === controls.length - 1)) {
          event.preventDefault();
          controls[event.shiftKey ? controls.length - 1 : 0].focus();
        }
      }
    }
  }

  customElements.define('spike-size-guide', SpikeSizeGuide);
  // The old theme handler opens every chart in MainContent. Resolve the product
  // context here instead; only claim triggers that have the new guide present.
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-open-size-chart-popup]');
    if (trigger) {
      const scope = trigger.closest('[data-quick-view-popup]') || trigger.closest('main');
      const guide = scope?.querySelector('[data-size-chart-popup] spike-size-guide');
      if (!guide) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      guide.open(trigger);
      return;
    }
    const close = event.target.closest('[data-close-size-chart-popup]');
    const guide = close?.closest('[data-size-chart-popup]')?.querySelector('spike-size-guide');
    if (guide) {
      event.preventDefault();
      event.stopImmediatePropagation();
      guide.close();
    }
  }, true);
  document.addEventListener('keydown', event => {
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-open-size-chart-popup]:not(button)')) {
      event.preventDefault();
      event.target.click();
    }
  });
})();
